import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Grid
} from '@mui/material';
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SportsbookAccountService } from '../services/sportsbookAccountService';

const AccountLinker = ({ compact = false }) => {
  const { currentUser } = useAuth();
  const [linkedAccounts, setLinkedAccounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSportsbook, setSelectedSportsbook] = useState(null);
  const [accountEmail, setAccountEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // All 11 sportsbooks configuration
  const sportsbooks = [
    {
      id: 'draftkings',
      name: 'DraftKings',
      logo: '/assets/draftkings-logo.png',
      loginUrl: 'https://sportsbook.draftkings.com',
      color: '#53d337'
    },
    {
      id: 'fanduel',
      name: 'FanDuel',
      logo: '/assets/fanduel-logo.png',
      loginUrl: 'https://sportsbook.fanduel.com',
      color: '#1e3a8a'
    },
    {
      id: 'betmgm',
      name: 'BetMGM',
      logo: '/assets/betmgm-logo.png',
      loginUrl: 'https://sports.betmgm.com',
      color: '#FFD700'
    },
    {
      id: 'betus',
      name: 'BetUS',
      logo: '/assets/betus-logo.png',
      loginUrl: 'https://www.betus.com.pa/sportsbook',
      color: '#FF4500'
    },
    {
      id: 'caesars',
      name: 'Caesars',
      logo: '/assets/caesars-logo.png',
      loginUrl: 'https://www.caesars.com/sportsbook',
      color: '#c8a96e'
    },
    {
      id: 'betrivers',
      name: 'BetRivers',
      logo: '/assets/betrivers-logo.png',
      loginUrl: 'https://ny.betrivers.com/?page=sportsbook',
      color: '#0066CC'
    },
    {
      id: 'fanatics',
      name: 'Fanatics',
      logo: '/assets/fanatics-logo.png',
      loginUrl: 'https://www.fanaticssportsbook.com',
      color: '#FF6600'
    },
    {
      id: 'mybookie',
      name: 'MyBookie.ag',
      logo: '/assets/mybookie-logo.png',
      loginUrl: 'https://mybookie.ag/sportsbook',
      color: '#000000'
    },
    {
      id: 'lowvig',
      name: 'LowVig.ag',
      logo: '/assets/lowvig-logo.png',
      loginUrl: 'https://www.lowvig.ag/sports',
      color: '#2E7D32'
    },
    {
      id: 'betonline',
      name: 'BetOnline.ag',
      logo: '/assets/betonline-logo.png',
      loginUrl: 'https://www.betonline.ag/sportsbook',
      color: '#DC143C'
    },
    {
      id: 'bovada',
      name: 'Bovada',
      logo: '/assets/bovada-logo.png',
      loginUrl: 'https://www.bovada.lv/sports',
      color: '#CC0000'
    }
  ];

  // Load linked accounts on mount
  useEffect(() => {
    if (currentUser) {
      loadLinkedAccounts();
    }
  }, [currentUser]);

  const loadLinkedAccounts = async () => {
    try {
      setLoading(true);
      const accounts = await SportsbookAccountService.getLinkedAccounts(currentUser.uid);
      
      // Convert array to object for easy lookup
      const accountsMap = {};
      accounts.forEach(account => {
        accountsMap[account.sportsbookId] = account;
      });
      
      setLinkedAccounts(accountsMap);
    } catch (err) {
      console.error('Error loading linked accounts:', err);
      setError('Failed to load linked accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkClick = (sportsbook) => {
    // Directly open sportsbook homepage in new tab
    window.open(sportsbook.loginUrl, '_blank', 'noopener,noreferrer');
  };

  const handleUnlinkClick = async (sportsbook) => {
    if (!window.confirm(`Are you sure you want to unlink your ${sportsbook.name} account?`)) {
      return;
    }

    try {
      setActionLoading(true);
      await SportsbookAccountService.unlinkAccount(currentUser.uid, sportsbook.id);
      setSuccess(`${sportsbook.name} account unlinked successfully`);
      await loadLinkedAccounts();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error unlinking account:', err);
      setError(`Failed to unlink ${sportsbook.name} account`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleGoToLogin = () => {
    if (selectedSportsbook) {
      // Open sportsbook login page in new tab
      window.open(selectedSportsbook.loginUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleConfirmLink = async () => {
    if (!selectedSportsbook) return;

    try {
      setActionLoading(true);
      setError('');
      
      await SportsbookAccountService.linkAccount(
        currentUser.uid,
        selectedSportsbook.id,
        selectedSportsbook.name,
        accountEmail
      );
      
      setSuccess(`${selectedSportsbook.name} account linked successfully!`);
      await loadLinkedAccounts();
      
      // Close dialog after short delay
      setTimeout(() => {
        setDialogOpen(false);
        setSuccess('');
      }, 1500);
    } catch (err) {
      console.error('Error linking account:', err);
      setError('Failed to link account. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSportsbook(null);
    setAccountEmail('');
    setError('');
  };

  const isLinked = (sportsbookId) => {
    return linkedAccounts[sportsbookId]?.status === 'connected';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && !dialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Sportsbook Cards */}
      <Grid container spacing={2}>
        {sportsbooks.map((sportsbook) => {
          const linked = isLinked(sportsbook.id);
          const accountData = linkedAccounts[sportsbook.id];

          return (
            <Grid item xs={12} key={sportsbook.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {sportsbook.name}
                      </Typography>
                      <Chip
                        icon={linked ? <CheckCircleIcon /> : <LinkOffIcon />}
                        label={linked ? 'Connected' : 'Not Connected'}
                        color={linked ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Box>
                      {linked ? (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<LinkOffIcon />}
                          onClick={() => handleUnlinkClick(sportsbook)}
                          disabled={actionLoading}
                        >
                          Unlink
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          startIcon={<LinkIcon />}
                          onClick={() => handleLinkClick(sportsbook)}
                        >
                          Link Account
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {linked && accountData?.accountEmail && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Account: {accountData.accountEmail}
                    </Typography>
                  )}

                  {linked && accountData?.linkedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Linked {new Date(accountData.linkedAt.toDate()).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Link Account Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Link {selectedSportsbook?.name} Account
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body1" gutterBottom sx={{ mt: 1 }}>
            To link your {selectedSportsbook?.name} account:
          </Typography>

          <List dense>
            <ListItem>
              <ListItemText
                primary="1. Click 'Go to Login' to open your sportsbook"
                secondary="This will open in a new tab"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="2. Log in to your account"
                secondary="Use your existing credentials"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="3. Enter your account email below (optional)"
                secondary="Helps you track which account is linked"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="4. Click 'Confirm Link'"
                secondary="This confirms the account is linked to Bettor Advantage"
              />
            </ListItem>
          </List>

          <TextField
            fullWidth
            label="Account Email/Username (Optional)"
            value={accountEmail}
            onChange={(e) => setAccountEmail(e.target.value)}
            margin="normal"
            placeholder="your.email@example.com"
            helperText="This is just for your reference - we don't store passwords"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={handleGoToLogin}
            disabled={actionLoading}
          >
            Go to Login
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmLink}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Confirm Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountLinker;
